import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { X, MessageSquare } from 'lucide-react';
import { supportApi } from '@/api/endpoints/support';
import { socketClient } from '@/api/socket';
import { useAuth } from '@/hooks/useAuth';
import {
  playMessageSound,
  playNotificationSound,
  playToqueUrgente,
  primeAudio,
} from '@/pages/Support/sound';
import {
  ALERTA_INSISTENTE,
  NOTIFICATION_TEXT,
  type NotificationKind,
  type PanelNotification,
} from './notificationLabels';

export type { PanelNotification } from './notificationLabels';

// Alerta que ainda está tocando, esperando alguém reagir.
export interface AlertaPendente {
  convId: number;
  kind: NotificationKind;
  title: string;
}

interface NotificationContextValue {
  notifications: PanelNotification[];
  unreadCount: number;
  // tempo real do suporte (consumido pela Central de Atendimento)
  connected: boolean;
  typing: Set<number>;
  myUserId: number | null;
  // Conversas com alerta tocando agora (o modal da Central lê daqui).
  alertasPendentes: AlertaPendente[];
  // O atendente reagiu à conversa (abriu, assumiu, dispensou): para o alerta.
  markRead: (convId: number) => void;
  dispensarAlerta: (convId: number) => void;
  markAllRead: () => void;
  clear: () => void;
  goToConversation: (convId: number) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const NotificationContext = createContext<NotificationContextValue | null>(null);

// O atendente está com a aba de Suporte aberta agora?
function onSupportTab(): boolean {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/suporte');
}

// Toque urgente a cada 3 s enquanto houver alerta pendente, por no máximo 3
// min desde o último alerta novo: depois disso o banner e o sino continuam,
// mas o som para (tocar a tarde inteira vira tortura, não aviso).
const INTERVALO_ALERTA_MS = 3000;
const TETO_ALERTA_MS = 3 * 60 * 1000;

// Abas do painel no mesmo navegador conversam por aqui: dispensar ou abrir a
// conversa numa aba para o som nas outras.
const CANAL_ABAS = 'curraltech-suporte-alertas';

interface StatusEvent {
  conversationId?: number;
  status?: string;
  reason?: string;
  transferTo?: number;
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { manager } = useAuth();
  const canSupport = !!manager?.permissions?.includes('support');
  const myUserId = manager?.id ?? null;
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<PanelNotification[]>([]);
  const [alertas, setAlertas] = useState<Map<number, AlertaPendente>>(new Map());
  const [connected, setConnected] = useState(false);
  // Indicador de "digitando" do cliente não existe no Gupshup. Mantido como
  // Set sempre vazio pra não quebrar o contrato da Central (typing.has(id)).
  const [typing] = useState<Set<number>>(new Set());
  const [banner, setBanner] = useState<PanelNotification | null>(null);

  const seq = useRef(0);
  const bannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ultimoAlertaEm = useRef(0);
  const canal = useRef<BroadcastChannel | null>(null);
  const myUserIdRef = useRef(myUserId);
  useEffect(() => {
    myUserIdRef.current = myUserId;
  }, [myUserId]);

  // Destrava o áudio no primeiro gesto do usuário (política dos browsers).
  useEffect(() => {
    const unlock = () => primeAudio();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // Para tudo que diz respeito à conversa: alerta, banner e o "não lido" no
  // sino. `propagar` avisa as outras abas.
  const resolverConversa = useCallback((convId: number, propagar = true) => {
    setAlertas((prev) => {
      if (!prev.has(convId)) return prev;
      const next = new Map(prev);
      next.delete(convId);
      return next;
    });
    setNotifications((prev) =>
      prev.some((n) => n.convId === convId && !n.read)
        ? prev.map((n) => (n.convId === convId ? { ...n, read: true } : n))
        : prev
    );
    setBanner((b) => (b && b.convId === convId ? null : b));
    if (propagar) canal.current?.postMessage({ tipo: 'resolver', convId });
  }, []);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const bc = new BroadcastChannel(CANAL_ABAS);
    bc.onmessage = (e: MessageEvent<{ tipo?: string; convId?: number }>) => {
      if (e.data?.tipo === 'resolver' && typeof e.data.convId === 'number') {
        resolverConversa(e.data.convId, false);
      }
    };
    canal.current = bc;
    return () => {
      bc.close();
      canal.current = null;
    };
  }, [resolverConversa]);

  // Loop do alerta insistente: liga quando há alerta pendente, desliga sozinho
  // quando o último é resolvido (em qualquer tela, por qualquer atendente).
  const temAlerta = alertas.size > 0;
  useEffect(() => {
    if (!temAlerta) return;
    const tocar = () => {
      if (Date.now() - ultimoAlertaEm.current > TETO_ALERTA_MS) return;
      playToqueUrgente();
    };
    tocar();
    const timer = window.setInterval(tocar, INTERVALO_ALERTA_MS);
    return () => window.clearInterval(timer);
  }, [temAlerta]);

  const pushNotification = useCallback((n: Omit<PanelNotification, 'id' | 'read' | 'at'>) => {
    seq.current += 1;
    const notif: PanelNotification = { ...n, id: seq.current, read: false, at: Date.now() };
    setNotifications((prev) => [notif, ...prev].slice(0, 50));
    setBanner(notif);

    if (ALERTA_INSISTENTE.has(n.kind)) {
      ultimoAlertaEm.current = Date.now();
      setAlertas((prev) =>
        new Map(prev).set(n.convId, { convId: n.convId, kind: n.kind, title: n.title })
      );
    } else {
      playNotificationSound();
    }

    if (bannerTimer.current) clearTimeout(bannerTimer.current);
    bannerTimer.current = setTimeout(() => setBanner(null), 20000);
  }, []);

  // Busca os dados do contato (nome/telefone) pra montar a notificação — o
  // evento do socket só carrega o id, nunca dado do cliente. Também é aqui que
  // se confere se ainda faz sentido avisar: conversa que já tem dono (outro
  // atendente foi mais rápido) ou já encerrou não toca nada.
  const notifyAbout = useCallback(
    async (conversationId: number, kind: NotificationKind) => {
      try {
        const conv = await supportApi.getConversation(conversationId);
        if (conv.status === 'resolved') return;
        const dono = conv.meta?.assignee?.id ?? null;
        if (kind === 'ready' && dono != null) return;
        if (kind === 'message' && dono !== myUserIdRef.current) return;
        pushNotification({
          source: 'support',
          kind,
          title: conv.meta?.sender?.name || conv.meta?.sender?.phone_number || 'Cliente',
          convId: conversationId,
        });
      } catch {
        // conversa pode ter sido resolvida/apagada entre o evento e a busca —
        // não é motivo pra quebrar nada, só não mostra o banner.
      }
    },
    [pushNotification]
  );

  useEffect(() => {
    if (!canSupport) return;

    const onConversationCreated = (data: unknown) => {
      const id = (data as { conversationId?: number })?.conversationId;
      qc.invalidateQueries({ queryKey: ['support', 'conversations'] });
      if (id != null) void notifyAbout(id, 'new');
    };

    const onTriageReady = (data: unknown) => {
      const id = (data as { conversationId?: number })?.conversationId;
      qc.invalidateQueries({ queryKey: ['support', 'conversations'] });
      if (id != null) void notifyAbout(id, 'ready');
    };

    const onMessageReceived = (data: unknown) => {
      const { conversationId: id, direction } =
        (data as { conversationId?: number; direction?: string }) || {};
      if (id != null) qc.invalidateQueries({ queryKey: ['support', 'messages', id] });
      qc.invalidateQueries({ queryKey: ['support', 'conversations'] });
      if (direction === 'out' || id == null) return;
      // Na Central o som curto basta; fora dela, só avisa se o atendimento é
      // de quem está olhando (mensagem de cliente dos outros não interrompe).
      if (onSupportTab()) playMessageSound();
      else void notifyAbout(id, 'message');
    };

    const onStatusUpdated = (data: unknown) => {
      const { conversationId: id, status, reason, transferTo } = (data as StatusEvent) || {};
      qc.invalidateQueries({ queryKey: ['support', 'conversations'] });
      if (id == null) return;
      qc.invalidateQueries({ queryKey: ['support', 'messages', id] });

      // Alguém assumiu, aceitou, transferiu ou encerrou: o alerta desta
      // conversa para em todas as telas, inclusive a de quem não clicou.
      if (
        reason === 'assign' ||
        reason === 'accept-transfer' ||
        reason === 'transfer' ||
        status === 'resolved'
      ) {
        resolverConversa(id, false);
      }
      if (reason === 'transfer' && transferTo != null && transferTo === myUserIdRef.current) {
        void notifyAbout(id, 'transfer');
      }
      // API antiga (sem `reason`): "open" era o único sinal de triagem pronta.
      // notifyAbout descarta se a conversa já tiver dono.
      if (!reason && status === 'open') void notifyAbout(id, 'ready');
    };

    // Voltou a conexão: o que foi emitido durante a queda se perdeu, então
    // recarrega tudo do suporte de uma vez.
    const onConnect = () => {
      setConnected(true);
      qc.invalidateQueries({ queryKey: ['support'] });
    };
    const onDisconnect = () => setConnected(false);

    socketClient.on('support:conversation-created', onConversationCreated);
    socketClient.on('support:triage-ready', onTriageReady);
    socketClient.on('support:message-received', onMessageReceived);
    socketClient.on('support:status-updated', onStatusUpdated);
    socketClient.on('connect', onConnect);
    socketClient.on('disconnect', onDisconnect);
    setConnected(socketClient.isConnected());

    return () => {
      socketClient.off('support:conversation-created', onConversationCreated);
      socketClient.off('support:triage-ready', onTriageReady);
      socketClient.off('support:message-received', onMessageReceived);
      socketClient.off('support:status-updated', onStatusUpdated);
      socketClient.off('connect', onConnect);
      socketClient.off('disconnect', onDisconnect);
    };
  }, [canSupport, qc, notifyAbout, resolverConversa]);

  const markRead = useCallback((convId: number) => resolverConversa(convId), [resolverConversa]);
  const dispensarAlerta = useCallback(
    (convId: number) => resolverConversa(convId),
    [resolverConversa]
  );
  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);
  const clear = useCallback(() => {
    setNotifications([]);
    setAlertas(new Map());
    setBanner(null);
  }, []);

  const goToConversation = useCallback(
    (convId: number) => {
      resolverConversa(convId);
      setBanner(null);
      navigate(`/suporte?conv=${convId}`);
    },
    [navigate, resolverConversa]
  );

  const alertasPendentes = useMemo(() => [...alertas.values()], [alertas]);

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications,
      unreadCount: notifications.filter((n) => !n.read).length,
      connected,
      typing,
      myUserId,
      alertasPendentes,
      markRead,
      dispensarAlerta,
      markAllRead,
      clear,
      goToConversation,
    }),
    [
      notifications,
      connected,
      typing,
      myUserId,
      alertasPendentes,
      markRead,
      dispensarAlerta,
      markAllRead,
      clear,
      goToConversation,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {/* Banner temporário — canto inferior direito, em qualquer página */}
      {banner && (
        <div
          role="status"
          aria-live="polite"
          className="animate-chamar-atencao fixed bottom-4 right-4 z-[100] w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border-2 border-secondary bg-card shadow-2xl"
        >
          <div className="flex items-start gap-3 p-4">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-tertiary/15 text-tertiary">
              <MessageSquare size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-text-title">{banner.title}</p>
              <p className="text-xs text-text-secondary">{NOTIFICATION_TEXT[banner.kind].longo}</p>
              <button
                type="button"
                onClick={() => goToConversation(banner.convId)}
                className="mt-2 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
              >
                Ver conversa
              </button>
            </div>
            <button
              type="button"
              onClick={() => dispensarAlerta(banner.convId)}
              className="shrink-0 rounded-md p-1 text-text-tertiary hover:bg-muted hover:text-text-title"
              aria-label="Dispensar"
            >
              <X size={16} />
            </button>
          </div>
          <div className="h-1 w-full bg-primary" />
        </div>
      )}
    </NotificationContext.Provider>
  );
}
